import {
  Badge, IconButton, Popover, Box, Typography, List, ListItem,
  ListItemText, Chip, Button, Divider, CircularProgress,
} from '@mui/material';
import { Notifications, Warning, Schedule, CheckCircle } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import api from '../../api/axios';

const typeIcon = {
  low_stock: <Warning color="warning" fontSize="small" />,
  expiry_alert: <Schedule color="error" fontSize="small" />,
};

const typeColor = {
  low_stock: 'warning',
  expiry_alert: 'error',
};

export default function NotificationsPanel() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.data.rows); setUnread(data.data.unread);
    } catch {}
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  const open = Boolean(anchorEl);

  const handleOpen = async (e) => {
    setAnchorEl(e.currentTarget);
    if (unread > 0) {
      await api.post('/notifications/mark-read', {});
      setUnread(0);
    }
  };

  const generateAlerts = async () => {
    setLoading(true);
    try {
      await api.post('/notifications/generate-alerts');
      await load();
    } finally { setLoading(false); }
  };

  return (
    <>
      <IconButton onClick={handleOpen}>
        <Badge badgeContent={unread || null} color="error">
          <Notifications />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxHeight: 480, overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
          <Typography fontWeight={700}>Notifications</Typography>
          <Button size="small" onClick={generateAlerts} disabled={loading}>{loading ? <CircularProgress size={16} /> : 'Refresh Alerts'}</Button>
        </Box>

        <List sx={{ overflow: 'auto', flex: 1, p: 0 }}>
          {notifications.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CheckCircle color="success" sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">All clear! No alerts.</Typography>
            </Box>
          )}
          {notifications.map((n, i) => (
            <Box key={n.id}>
              <ListItem alignItems="flex-start" sx={{ bgcolor: n.is_read ? 'inherit' : 'action.hover' }}>
                <Box sx={{ mr: 1.5, mt: 0.5 }}>{typeIcon[n.type] || <Notifications fontSize="small" />}</Box>
                <ListItemText
                  primary={<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight={600}>{n.title}</Typography>
                    <Chip label={n.type.replace('_', ' ')} size="small" color={typeColor[n.type] || 'default'} sx={{ ml: 1, fontSize: 10 }} />
                  </Box>}
                  secondary={<>
                    <Typography variant="caption">{n.message}</Typography>
                    <Typography variant="caption" color="text.disabled" display="block">
                      {new Date(n.created_at).toLocaleString('en-PK')}
                    </Typography>
                  </>}
                />
              </ListItem>
              {i < notifications.length - 1 && <Divider component="li" />}
            </Box>
          ))}
        </List>
      </Popover>
    </>
  );
}
